import classnames from 'classnames';
import { ButtonRounded, Loader, TextInput } from 'is24-corecss';
import React from 'react';
import { useForm } from 'react-hook-form';
import styles from './styles';
import { isEqual } from '@/utils/isEqual.util';
import { useIntl } from '@/i18n/hooks/useIntl';
import { useUpdateInsuranceDetails } from '@/apiHooks/insurance.hook';
import type { InsuranceDetails, InsuranceServiceName, UpdateInsuranceDetails } from '@/@types/insurance';
import { report } from '@/tracking/tracking';
import { TrackingEvents } from '@/tracking/trackingEvents';
import { useHasMeinPlus } from '@/customHooks/useHasMeinPlus.hook';

export interface AddressModifyFormProps {
  close: () => void;
  setModificationForm: (modification: boolean) => void;
  isLoading: boolean;
  serviceName: InsuranceServiceName;
  insuranceDetails: InsuranceDetails;
  addressPrefill?: Partial<NonNullable<UpdateInsuranceDetails['address']>>;
  onSaved?: (insuranceDetails: UpdateInsuranceDetails) => void;
  onSaveError?: () => void;
}

export const AddressModifyForm: React.FC<AddressModifyFormProps> = ({
  close,
  setModificationForm,
  isLoading,
  serviceName,
  insuranceDetails,
  addressPrefill,
  onSaved,
  onSaveError,
}) => {
  const { t } = useIntl();
  const { mutate: mutateUpdateDetails } = useUpdateInsuranceDetails(insuranceDetails.insuranceNumber);
  const isMeinPlus = useHasMeinPlus();
  const address = addressPrefill ?? insuranceDetails.address;

  const defaultValues = {
    firstName: insuranceDetails.user?.firstName || '',
    lastName: insuranceDetails.user?.lastName || '',
    city: address?.city || '',
    houseNumber: address?.houseNumber || '',
    street: address?.street || '',
    postcode: address?.postalCode || '',
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({ defaultValues, values: defaultValues, mode: 'all', criteriaMode: 'all' });

  const onSubmit = async (values: typeof defaultValues) => {
    try {
      const formDetails: UpdateInsuranceDetails = {
        user: {
          firstName: values.firstName,
          lastName: values.lastName,
        },
        address: {
          street: values.street,
          houseNumber: values.houseNumber,
          postalCode: values.postcode,
          city: values.city,
        },
      };

      const isAddressChanged = !isEqual(formDetails.address, address);
      const isUserChanged = !isEqual(formDetails.user, insuranceDetails.user);
      if (isAddressChanged || isUserChanged) {
        await mutateUpdateDetails({
          insuranceDetails: formDetails,
        });
        onSaved?.(formDetails);
      }

      setModificationForm(false);
    } catch {
      onSaveError?.();
      if (!onSaveError) setModificationForm(false);
    } finally {
      await report(TrackingEvents[serviceName].FUNNEL_MODIFY, isMeinPlus);
    }
  };

  return (
    <form className={styles.aragModifyContainer} onSubmit={handleSubmit(onSubmit)} data-testid='AddressModifyForm'>
      <div className={styles.content}>
        <div className={styles.column}>
          {isLoading && (
            <div className={styles.loaderContainer} data-testid='loader'>
              <Loader />
            </div>
          )}
          <>
            <div className={styles.row}>
              <TextInput.Root id='firstName' invalid={!!errors.firstName}>
                <TextInput.Label>
                  {t({
                    id: 'common.aragModal.form.firstName.label',
                    defaultMessage: 'Vorname',
                  })}
                </TextInput.Label>
                <TextInput.Input
                  placeholder=''
                  {...register('firstName', { required: true })}
                  data-testid='firstName'
                />
              </TextInput.Root>
            </div>
            <div className={styles.row}>
              <TextInput.Root id='lastName' invalid={!!errors.lastName}>
                <TextInput.Label>
                  {t({
                    id: 'common.aragModal.form.lastName.label',
                    defaultMessage: 'Nachname',
                  })}
                </TextInput.Label>
                <TextInput.Input {...register('lastName', { required: true })} placeholder='' data-testid='lastName' />
              </TextInput.Root>
            </div>
            <div className={classnames(styles.address1, styles.row)}>
              <div className={styles.street}>
                <TextInput.Root id='street' invalid={!!errors.street}>
                  <TextInput.Label>
                    {t({
                      id: 'common.aragModal.form.street.label',
                      defaultMessage: 'Straße',
                    })}
                  </TextInput.Label>
                  <TextInput.Input {...register('street', { required: true })} placeholder='' data-testid='street' />
                </TextInput.Root>
              </div>
              <div className={styles.houseNumber}>
                <TextInput.Root id='houseNumber' invalid={!!errors.houseNumber}>
                  <TextInput.Label>
                    {t({
                      id: 'common.aragModal.form.houseNumber.label',
                      defaultMessage: 'Hausnr.',
                    })}
                  </TextInput.Label>
                  <TextInput.Input
                    placeholder=''
                    {...register('houseNumber', { required: true })}
                    data-testid='houseNumber'
                  />
                </TextInput.Root>
              </div>
            </div>
            <div className={classnames(styles.row, styles.address2)}>
              <div className={styles.postcode}>
                <TextInput.Root id='postcode' invalid={!!errors.postcode}>
                  <TextInput.Label>
                    {t({
                      id: 'common.aragModal.form.postcode.label',
                      defaultMessage: 'PLZ',
                    })}
                  </TextInput.Label>
                  <TextInput.Input
                    placeholder=''
                    {...register('postcode', { required: true, pattern: /^[0-9]{5}$/ })}
                    data-testid='postcode'
                  />
                </TextInput.Root>
              </div>
              <div className={styles.city}>
                <TextInput.Root id='city' invalid={!!errors.city}>
                  <TextInput.Label>
                    {t({
                      id: 'common.aragModal.form.city.label',
                      defaultMessage: 'Ort',
                    })}
                  </TextInput.Label>
                  <TextInput.Input placeholder='' {...register('city', { required: true })} data-testid='city' />
                </TextInput.Root>
              </div>
            </div>
          </>
        </div>
      </div>
      <div className={styles.modifyFooter}>
        <ButtonRounded onClick={close} type='button' variant='textDefault' className='margin-right-m button-link'>
          {t({ id: 'common.aragModal.form.cancelButtonText', defaultMessage: 'Abbrechen' })}
        </ButtonRounded>
        <ButtonRounded type='submit' disabled={isLoading}>
          {t({ id: 'common.aragModal.form.saveButtonText', defaultMessage: 'Speichern' })}
        </ButtonRounded>
      </div>
    </form>
  );
};
